import React from 'react';
import { ScrollView, StyleSheet, View, TouchableOpacity, Text, Platform } from 'react-native';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { WebConfirmModal } from '@/components/common/modals';
import { ADMIN_COLORS } from '@/constants/admin';
import { DESKTOP_CONFIRM_COPY } from '@/constants/admin/desktop';

interface DesktopFormPageProps {
  title: string;
  subtitle?: string;
  isSubmitting?: boolean;
  submitText?: string;
  onSubmit: () => void;
  onCancel: () => void;
  confirmDiscardOnWeb?: boolean;
  discardTitle?: string;
  discardMessage?: string;
  children: React.ReactNode;
  contentMaxWidth?: number;
  renderHeaderActions?: () => React.ReactNode;
}

export const DesktopFormPage: React.FC<DesktopFormPageProps> = ({
  title,
  subtitle,
  isSubmitting,
  submitText = 'Save',
  onSubmit,
  onCancel,
  confirmDiscardOnWeb = true,
  discardTitle = DESKTOP_CONFIRM_COPY.discardTitle,
  discardMessage = DESKTOP_CONFIRM_COPY.discardMessage,
  children,
  contentMaxWidth = 880,
  renderHeaderActions,
}) => {
  const [showDiscardModal, setShowDiscardModal] = React.useState(false);

  const handleCancelPress = () => {
    if (confirmDiscardOnWeb && Platform.OS === 'web') {
      setShowDiscardModal(true);
      return;
    }
    onCancel();
  };

  return (
    <DesktopAdminLayout>
      {confirmDiscardOnWeb && (
        <WebConfirmModal
          visible={showDiscardModal}
          title={discardTitle}
          message={discardMessage}
          confirmText={DESKTOP_CONFIRM_COPY.discardConfirmText}
          cancelText={DESKTOP_CONFIRM_COPY.discardCancelText}
          onConfirm={() => {
            setShowDiscardModal(false);
            onCancel();
          }}
          onCancel={() => setShowDiscardModal(false)}
        />
      )}

      <DesktopPageHeader title={title} subtitle={subtitle} showAddButton={false}>
        {renderHeaderActions ? (
          renderHeaderActions()
        ) : (
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <TouchableOpacity
              onPress={handleCancelPress}
              style={styles.secondaryBtn}
            >
              <Text style={styles.secondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={onSubmit}
              disabled={isSubmitting}
              style={[styles.primaryBtn, isSubmitting ? { opacity: 0.7 } : null]}
            >
              <Text style={styles.primaryText}>{submitText}</Text>
            </TouchableOpacity>
          </View>
        )}
      </DesktopPageHeader>

      <ScrollView
        style={styles.desktopScroll}
        contentContainerStyle={[styles.desktopScrollContent, { maxWidth: contentMaxWidth, alignSelf: 'center', width: '100%' }]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {children}
      </ScrollView>
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  desktopScroll: {
    flex: 1,
  },
  desktopScrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 24,
    gap: 16,
  },
  secondaryBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  secondaryText: {
    color: ADMIN_COLORS.TEXT_PRIMARY,
    fontWeight: '600',
  },
  primaryBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  primaryText: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
});


